import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Link } from 'react-router-dom';
import { getSelfAiAnalysis, getSelfAiContext, ReportSection } from '@/services/self-reports.service';
import { getClientActivityAiAnalysis, getClientActivityAiContext } from '@/services/client-reports.service';
import Chip from '@mui/material/Chip';
import CachedIcon from '@mui/icons-material/Cached';
import { toastService } from '@/services/toast.service';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { ReportOptionsPrompt } from '@/types/client-reports';
import { htmlToMarkdown } from '@/utils/html-to-markdown';

type PromptMode = 'default' | 'library' | 'custom';

interface AiAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  from: string;
  to: string;
  sections?: ReportSection[];
  clientId?: string; // Якщо передано — режим експерта
  prompts?: ReportOptionsPrompt[];
}

export const AiAnalysisModal: React.FC<AiAnalysisModalProps> = ({ open, onClose, from, to, sections, clientId, prompts = [] }) => {
  const isExpertMode = !!clientId;

  const [prompt, setPrompt] = useState('');
  const [promptMode, setPromptMode] = useState<PromptMode>(isExpertMode ? 'library' : 'default');
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(isExpertMode ? 0 : null);
  const [analysisText, setAnalysisText] = useState('');
  const [fromCache, setFromCache] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisCopied, setAnalysisCopied] = useState(false);

  const [contextOpen, setContextOpen] = useState(false);
  const [contextMarkdown, setContextMarkdown] = useState('');
  const [loadingContext, setLoadingContext] = useState(false);
  const [copied, setCopied] = useState(false);

  // Скидаємо вибір при відкритті, якщо це режим експерта
  useEffect(() => {
    if (open && isExpertMode) {
      setPromptMode('library');
      setSelectedPromptId(0);
    }
  }, [open, isExpertMode]);

  // Завдання для AI (2026-09-30): бібліотечний промпт - лише id (текст бекенд бере з бібліотеки експерта),
  // власний текст - як є, інакше нічого (бекенд візьме системне завдання).
  const effectiveTask = (): { prompt?: string; promptId?: number } => {
    if (promptMode === 'custom') return { prompt: prompt.trim() || undefined };
    if (promptMode === 'library' && selectedPromptId && prompts.some((p) => p.id === selectedPromptId)) {
      return { promptId: selectedPromptId };
    }
    return {};
  };

  const handleClose = () => {
    if (loadingAnalysis) return;
    setPrompt('');
    setPromptMode(isExpertMode ? 'library' : 'default');
    setSelectedPromptId(isExpertMode ? 0 : null);
    setAnalysisText('');
    setFromCache(false);
    setAnalysisCopied(false);
    onClose();
  };

  const handleCopyAnalysis = () => {
    navigator.clipboard.writeText(htmlToMarkdown(analysisText)).then(() => {
      setAnalysisCopied(true);
      setTimeout(() => setAnalysisCopied(false), 2000);
    });
  };

  const handleRunAnalysis = async () => {
    setLoadingAnalysis(true);
    setAnalysisText('');
    setFromCache(false);
    setAnalysisCopied(false);
    try {
      console.log('Running AI analysis...', { clientId, from, to, sections, ...effectiveTask() });
      const result = isExpertMode
        ? await getClientActivityAiAnalysis(clientId, from, to, sections, effectiveTask())
        : await getSelfAiAnalysis(from, to, sections, prompt.trim() || undefined);
      
      console.log('AI Analysis result:', result);
      setAnalysisText(result.html);
      setFromCache(result.fromCache);
    } catch (e: any) {
      console.error('AI Analysis error:', e);
      toastService.error(e?.message ?? 'Не вдалося отримати AI-аналіз');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleOpenContext = async () => {
    setLoadingContext(true);
    setContextMarkdown('');
    setContextOpen(true);
    try {
      console.log('Fetching AI context...', { clientId, from, to, sections });
      const md = isExpertMode
        ? await getClientActivityAiContext(clientId, from, to, sections)
        : await getSelfAiContext(from, to, sections);
      
      console.log('AI Context received:', md?.substring(0, 100) + '...');
      setContextMarkdown(md);
    } catch (e) {
      console.error('AI Context error:', e);
      toastService.error('Не вдалося завантажити AI-контекст');
      setContextOpen(false);
    } finally {
      setLoadingContext(false);
    }
  };

  const handleDownload = () => {
    const html = `<!DOCTYPE html>
<html lang="uk">
<head>
  <meta charset="UTF-8" />
  <title>AI-аналіз ${from} – ${to}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 800px; margin: 40px auto; padding: 0 24px; color: #1a1a1a; line-height: 1.7; }
    h2, h3 { margin-top: 1.5em; margin-bottom: 0.4em; }
    p { margin: 0 0 0.8em; }
    ul, ol { padding-left: 1.5em; margin-bottom: 0.8em; }
    li { margin-bottom: 0.2em; }
    strong { font-weight: 600; }
  </style>
</head>
<body>${analysisText}</body>
</html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-analysis_${from}_${to}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth data-i-doc={ModalIndices.AI_ANALYSIS}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesomeIcon fontSize="small" />
            AI-аналіз {isExpertMode ? 'клієнта' : ''}
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={loadingAnalysis}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Вибір режиму промпту — тільки для експерта */}
            {isExpertMode && (
              <>
                <ToggleButtonGroup
                  value={promptMode}
                  exclusive
                  onChange={(_, v) => { if (v) { setPromptMode(v); setSelectedPromptId(v === 'library' ? 0 : null); } }}
                  size="small"
                  disabled={loadingAnalysis}
                >
                  <ToggleButton value="library" sx={{ gap: 0.5 }}>
                    <AutoFixHighIcon sx={{ fontSize: 15 }} />
                    Бібліотека
                  </ToggleButton>
                  <ToggleButton value="custom">Власний</ToggleButton>
                </ToggleButtonGroup>

                {promptMode === 'library' && (
                  prompts.length > 0 ? (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                      <List disablePadding dense>
                        {prompts.map((p, i) => (
                          <React.Fragment key={p.id}>
                            {i > 0 && <Divider />}
                            <ListItemButton
                              selected={selectedPromptId === p.id}
                              onClick={() => setSelectedPromptId(p.id)}
                              disabled={loadingAnalysis}
                            >
                              <ListItemText
                                primary={p.title}
                                secondary={
                                  p.content ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}
                                    >
                                      {p.content}
                                    </Typography>
                                  ) : p.id === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                      Використовується стандартний алгоритм аналізу
                                    </Typography>
                                  ) : null
                                }
                              />
                            </ListItemButton>
                          </React.Fragment>
                        ))}
                      </List>
                    </Box>
                  ) : (
                    <Alert
                      severity="info"
                      action={
                        <Button size="small" component={Link} to="/expert/prompts">
                          Додати →
                        </Button>
                      }
                    >
                      Бібліотека порожня. Додайте промпти, щоб використовувати їх тут.
                    </Alert>
                  )
                )}
              </>
            )}

            {/* Текстове поле — в client-режимі завжди, в expert-режимі лише при 'custom' */}
            {(!isExpertMode || promptMode === 'custom') && (
              <TextField
                multiline
                minRows={2}
                maxRows={5}
                placeholder="Необов'язково: введіть свою інструкцію для AI. Наприклад: «Ти нутриціолог. Зроби акцент на харчуванні.» Якщо залишити порожнім — використовується стандартний аналіз."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={loadingAnalysis}
                fullWidth
              />
            )}

            {loadingAnalysis && (
              <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={3}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">Аналізую дані...</Typography>
              </Box>
            )}

            {!loadingAnalysis && analysisText && (
              <>
                {fromCache && (
                  <Box>
                    <Chip
                      icon={<CachedIcon fontSize="small" />}
                      label="Результат з кешу"
                      size="small"
                      variant="outlined"
                      color="default"
                    />
                  </Box>
                )}
                <Box
                  dangerouslySetInnerHTML={{ __html: analysisText }}
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: 1.7,
                    color: 'text.primary',
                    '& h2, & h3': { mt: 2, mb: 0.5, fontWeight: 600 },
                    '& p': { mt: 0, mb: 1 },
                    '& ul, & ol': { pl: 3, mb: 1 },
                    '& li': { mb: 0.25 },
                  }}
                />
                {!isExpertMode && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderColor: 'primary.light',
                      bgcolor: 'primary.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Хочете більш детальну консультацію?</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Цей аналіз сформований AI. Персональну консультацію з урахуванням вашої ситуації може надати живий експерт.
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" href="/experts" onClick={handleClose}>
                      Знайти експерта
                    </Button>
                  </Paper>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button startIcon={<PsychologyIcon />} onClick={handleOpenContext} disabled={loadingContext || loadingAnalysis}>
            AI-контекст
          </Button>
          {analysisText && (
            <>
              <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
                Завантажити
              </Button>
              <Button
                sx={{ mr: 'auto' }}
                startIcon={analysisCopied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                onClick={handleCopyAnalysis}
                color={analysisCopied ? 'success' : 'inherit'}
              >
                {analysisCopied ? 'Скопійовано' : 'Копіювати'}
              </Button>
            </>
          )}
          <Button onClick={handleClose} disabled={loadingAnalysis}>Закрити</Button>
          <Button
            variant="contained"
            startIcon={loadingAnalysis ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleRunAnalysis}
            disabled={loadingAnalysis}
          >
            {analysisText ? 'Повторити' : 'Аналізувати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI-контекст вкладена модалка */}
      <Dialog open={contextOpen} onClose={() => setContextOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <PsychologyIcon fontSize="small" />
            AI-контекст
          </Box>
          <IconButton size="small" onClick={() => setContextOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingContext ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}
            >
              {contextMarkdown}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ mr: 'auto' }}
            startIcon={copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            onClick={() => {
              navigator.clipboard.writeText(contextMarkdown).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }}
            disabled={!contextMarkdown}
            color={copied ? 'success' : 'inherit'}
          >
            {copied ? 'Скопійовано' : 'Копіювати'}
          </Button>
          <Button onClick={() => setContextOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
